import { LinkProvider } from '@astryxdesign/core/Link';
import { LayerProvider } from '@astryxdesign/core/Layer';
import { Theme } from '@astryxdesign/core/theme';
import { neutralTheme } from '@astryxdesign/theme-neutral/built';
import { Link } from '@inertiajs/react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getThemeMode, setThemeMode, type ThemeMode } from '@/lib/theme';
import { DirtyGuardProvider } from '@/components/dirty-guard';

interface ThemeModeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode() {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error('useThemeMode must be used within AstryxProviders');
  }

  return context;
}

interface AstryxProvidersProps {
  children: ReactNode;
}

export default function AstryxProviders({ children }: AstryxProvidersProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => getThemeMode());

  const setMode = (nextMode: ThemeMode) => {
    setModeState(nextMode);
    setThemeMode(nextMode);
  };

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'theme') {
        setModeState(getThemeMode());
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode }}>
      <Theme theme={neutralTheme} mode={mode}>
        <LinkProvider component={Link}>
          <LayerProvider>
            <DirtyGuardProvider>{children}</DirtyGuardProvider>
          </LayerProvider>
        </LinkProvider>
      </Theme>
    </ThemeModeContext.Provider>
  );
}
